<template>
  <v-container fluid>
    <!-- Title -->
    <Title
        title="Expedientes por validar"
        description="Lista de expedientes que requieren validación."
    />

    <!-- Validation Table -->
    <v-row>
      <v-col cols="12">
        <v-data-table
            class="border-px"
            :headers="headers"
            :items="clients"
            :items-per-page="itemsPerPage"
            :page.sync="pageNumber"
            :loading="isLoading"
            :server-items-length="totalItems"
            disable-filtering
            disable-sort
            no-data-text="No hay expedientes por validar."
            no-results-text="No se encontraron resultados."
            loadingText="Cargando expedientes..."
            :footer-props="{
              itemsPerPageOptions: [5, 10, 15, 20, 50],
              showFirstLastPage: true,
              itemsPerPageText: 'Expedientes por página',
              itemsPerPageAllText: 'Todos',
              pageText: `Página ${pageNumber} de ${totalPages}`,
              rowsPerPageText: 'Expedientes por página',
            }"
            @update:items-per-page="itemsPerPage = $event; getClientsForValidation()"
            @update:page="getClientsForValidation()"
        >
          <template v-slot:item.name="{ item }">
            {{ item.name }}
          </template>
          <template v-slot:item.office="{ item }">
            <span v-if="item.office" class="deep-orange--text font-weight-medium">DIETEC</span>
            {{ item.office || 'Sin consultorio' }}
          </template>
          <template v-slot:item.initialWeightKg="{ item }">
            {{ item.initialWeightKg.toFixed(3) }} kg
          </template>
          <template v-slot:item.nextAppointment="{ item }">
            {{ formatDateTime(item.nextAppointment) }}
          </template>
          <template v-slot:item.type="{ item }">
            <v-chip small :ripple="false" :class="clientTypeColor(item.type)">
              {{ item.type === 'REMOTE' ? 'En línea' : 'Presencial' }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn color="primary" small @click="openConfirmationDialog(item)">
              <v-icon left>mdi-check</v-icon>
              Validar
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <DialogConfirmation
        :message="`¿Estás seguro de que deseas validar el expediente de ${selectedClient ? selectedClient.name : ''}?`"
        @ok="validateClient"
        ref="dialogConfirmation"
    />
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue'

import Title from "@/components/Title.vue";
import DialogConfirmation from "@/components/DialogConfirmation.vue";

import {ClientForValidation} from "@/services/validations/entities/ClientForValidation";

import ValidationServices from "@/services/validations/ValidationServices";

import {clientTypeColorClass, formatDateTimeToDisplay} from "@/utils/UniversalFunctions";
import throwSuccessMessage from "@/utils/SuccessMessage";

export default Vue.extend({
  name: 'ValidationTable',
  components: {
    Title,
    DialogConfirmation,
  },
  data() {
    return {
      // Table
      clients: [] as ClientForValidation[],
      totalItems: 0 as number,
      totalPages: 0 as number,
      pageNumber: 1 as number,
      itemsPerPage: 10 as number,
      headers: [
        {text: 'Nombre', value: 'name', sortable: false},
        {text: 'Consultorio', value: 'office', sortable: false},
        {text: 'Peso inicial (kg)', value: 'initialWeightKg', sortable: false},
        {text: 'Siguiente cita', value: 'nextAppointment', sortable: false},
        {text: 'Tipo', value: 'type', sortable: false},
        {text: 'Validar', value: 'actions', sortable: false},
      ],

      // Client
      selectedClient: null as ClientForValidation | null,

      // Loading
      isLoading: false as boolean,
    }
  },

  methods: {
    async getClientsForValidation() {
      this.isLoading = true;
      try {
        const requestParams = new URLSearchParams({
          page: (this.pageNumber - 1).toString(),
          size: this.itemsPerPage.toString(),
        });
        const response = await ValidationServices.getClientsForValidation(requestParams);
        this.clients = response.content;
        this.totalItems = response.totalElements;
        this.totalPages = response.totalPages;
      } finally {
        this.isLoading = false;
      }
    },
    async validateClient() {
      if (!this.selectedClient) return;
      this.isLoading = true;
      try {
        const body = {
          idClient: this.selectedClient.idClient,
        }
        await ValidationServices.validateClient(body);
        throwSuccessMessage(`El expediente de ${this.selectedClient.name} ha sido validado exitosamente.`);
        await this.getClientsForValidation();
      } finally {
        this.isLoading = false;
      }
    },
    openConfirmationDialog(client: ClientForValidation) {
      this.selectedClient = client;
      (this.$refs.dialogConfirmation as any).dialog = true;
    },
    formatDateTime(date: Date) {
      return formatDateTimeToDisplay(date);
    },
    clientTypeColor(type: string) {
      return clientTypeColorClass(type);
    }
  },

  mounted() {
    this.getClientsForValidation();
  }
})
</script>

<style scoped>
.border-px {
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}
</style>